import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react'
import { User } from '@/types/user'
import { authService } from '@/services/authService'

interface AuthContextType {
  user: User | null
  loading: boolean
  permissions: string[]
  login: (email: string, password: string) => Promise<void>
  logout: () => void
  refreshToken: () => Promise<void>
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

interface AuthProviderProps {
  children: ReactNode
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null)
  const [loading, setLoading] = useState(true)
  const [permissions, setPermissions] = useState<string[]>([])

  useEffect(() => {
    const initializeAuth = async () => {
      try {
        const token = localStorage.getItem('accessToken')
        if (token) {
          const userData = await authService.getProfile()
          setUser(userData)
          // Fetch permissions
          try {
            const perms = await authService.getMyPermissions()
            setPermissions(perms)
          } catch (permError) {
            console.error('Failed to fetch permissions:', permError)
            // Don't set fallback permissions - let the user see an error if permissions fail
            setPermissions([])
          }
        } 
      } catch (error) {
        console.error('Failed to initialize auth:', error)
        localStorage.removeItem('accessToken')
        localStorage.removeItem('refreshToken')
      } finally {
        setLoading(false)
      }
    }

    initializeAuth()
  }, [])

  const login = async (email: string, password: string) => {
    try {
      const response = await authService.login({ email, password })
      setUser(response.user)
      localStorage.setItem('accessToken', response.accessToken)
      localStorage.setItem('refreshToken', response.refreshToken)
      // Fetch permissions
      try {
        const perms = await authService.getMyPermissions()
        setPermissions(perms)
      } catch (permError) {
        console.error('Failed to fetch permissions after login:', permError)
        // Don't set fallback permissions - let the user see an error if permissions fail
        setPermissions([])
      }
    } catch (error) {
      console.error('Login failed:', error)
      throw error
    }
  }

  const logout = () => {
    setUser(null)
    setPermissions([])
    localStorage.removeItem('accessToken')
    localStorage.removeItem('refreshToken')
  }

  const refreshToken = async () => {
    try {
      const refreshToken = localStorage.getItem('refreshToken')
      if (!refreshToken) {
        throw new Error('No refresh token available')
      }

      const response = await authService.refreshToken(refreshToken)
      localStorage.setItem('accessToken', response.accessToken)
      localStorage.setItem('refreshToken', response.refreshToken)
      // Optionally, refresh permissions here if needed
    } catch (error) {
      console.error('Token refresh failed:', error)
      logout()
      throw error
    }
  }

  const value: AuthContextType = {
    user,
    loading,
    permissions,
    login,
    logout,
    refreshToken,
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
} 